import React from 'react';
import {
  useSelector,
} from 'react-redux';
import { useField } from 'formik';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styles from './index.scss';
import Toggle from '../../toggle/index';
import HelloModal from '../../../../common/components/hello-modal';
import Button, { ButtonType } from 'hellospa/foundation/control/button';
import { ArrowDirection, TooltipPoint } from '../../../foundation/control/tooltip';
import messages from './intl';

const QESToggle = () => {
  const intl = useIntl();
  const learnMoreLink = 'https://faq.hellosign.com/hc/en-us/articles/360056581532-Qualified-eSignature-';
  const allowQES = useSelector(selectors.allowQES);
  const isSendingTemplate = useSelector(selectors.isSendingTemplate);
  const templateOneOffFile = useSelector(selectors.hasTemplateOneOffFile);
  const hasSignatureFields = useSelector(selectors.hasSignatureFields);

  const [isOpen, toggleOpen] = React.useState(false);
  const [QESField, , QESToggle] = useField<boolean>('qes');
  const [recipientsField] = useField<unknown[]>('recipients');
  const [recipientReassignment] = useField<boolean>('recipientReassignment');

  const modalMessages = defineMessages({
    modalContentLabel: {
      id: '',
      description: 'QES modal, title in modal body',
      defaultMessage: 'Enabling Qualified eSignature',
    },
  });

  let message;
  let disabled = false;

  if (isSendingTemplate && !templateOneOffFile && !hasSignatureFields) {
    message = intl.formatMessage(messages.qesWithNoSignatureTemplateTooltip);
    disabled = true;
  } else if (recipientReassignment.value) {
    message = intl.formatMessage(messages.qesWithReassignTooltip);
    disabled = true;
  } else if (recipientsField.value.length > 1) {
    message = intl.formatMessage(messages.qesWithMultiSignerTooltip);
    disabled = true;
  }

  React.useEffect(() => {
    if (disabled && QESField.value) {
      QESToggle.setValue(false);
    }
  }, [disabled, QESToggle, QESField.value]);

  if (!allowQES) {
    return null;
  }

  return (
    <div className={styles.signerOptionsContainer}>
      <label className={styles.ssToggleLabel} >
        {
          disabled
          && <TooltipPoint className={styles.tooltipUp} arrow={ArrowDirection.Top}>
              {message}
          </TooltipPoint>
        }
        <Toggle
          id="QESToggle"
          indeterminate={false}
          disabled={disabled}
          className={styles.ssToggle}
          {...QESField}
          onChange={(e) => {
            if (e.target.checked) {
              toggleOpen(true);
            }
            return QESField.onChange(e);
          }}
          value=""
          checked={QESField.value}
          data-qa-ref="recipients.fields.qes" />
        <div className={styles.ssLabelText}>
          <FormattedMessage id='' description='QES toggle, main label text' defaultMessage='Enable Qualified eSignature' />
          &nbsp;
          <a href={learnMoreLink} target='_blank' rel='noopener noreferrer'>
            <FormattedMessage id='' description='QES toggle, learn more link in label' defaultMessage='learn more' />
          </a>
        </div>
      </label>
      <HelloModal
        isOpen={isOpen}
        onRequestClose={() => {
          toggleOpen(false);
          QESToggle.setValue(false);
        }}
        buttons={[
          <Button
            key='Cancel'
            kind={ButtonType.Subtle}
            onClick={() => {
              QESToggle.setValue(false);
              toggleOpen(false);
            }}
            data-qa-ref="recipients.dialog.qes.cancel"
          ><FormattedMessage id='' description='QES modal, option to cancel' defaultMessage='No Thanks' /></Button>,
          <Button
            key='Ok'
            kind={ButtonType.Primary}
            onClick={() => {
              QESToggle.setValue(true);
              toggleOpen(false);
            }}
            data-qa-ref="recipients.dialog.qes.ok"
          >
            <FormattedMessage id='' description='QES modal, option to enable' defaultMessage='Enable' />
          </Button>,
        ]}
        contentLabel={intl.formatMessage(modalMessages.modalContentLabel)}
      >
        <FormattedMessage id='' description='QES modal, text in modal body' defaultMessage='This will require the signer to verify their identity with IDnow.' />
        <br />
        <a href={learnMoreLink} target='_blank' rel='noopener noreferrer'>
          <FormattedMessage id='' description='QES modal, text in modal body, learn more link' defaultMessage='Learn more' />
        </a>
      </HelloModal>
    </div>
  );
};

export default QESToggle;
